'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import { twJoin } from 'tailwind-merge';
import 'react';
import SystemIcon from '../../DataDisplays/SystemIcon/SystemIcon.js';
import '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';
import IconButton from '../IconButton/IconButton.js';
import List from '../List/List.js';
import useDropdown from '../../../hooks/useDropdown.js';

/**
 * 아이콘 버튼과 드롭다운 목록을 결합한 컴포넌트입니다.
 * 버튼 클릭 시 드롭다운 목록이 표시되며, 키보드 탐색이 가능합니다.
 *
 * @component
 *
 * @param {Object} props
 * @param {SystemIconName} props.icon - 버튼에 표시될 아이콘
 * @param {boolean} [props.disabled=false] - 버튼 비활성화 여부를 지정합니다
 * @param {('sm'|'md'|'lg')} [props.size='sm'] - 버튼의 크기
 * @param {('left'|'right')} [props.align='left'] - 드롭다운 목록의 정렬 방향
 * @param {Item[]} props.items - 드롭다운 목록에 표시될 항목들 ({key, value, leadingIcon}[])
 * @param {Item} props.selectedItem - 현재 선택된 항목
 * @param {(item: Item) => void} props.onItemClick - 항목 선택 시 실행될 콜백 함수
 *
 * @example
 * ```tsx
 * const items = [
 *   { key: 1, value: '옵션 1', leadingIcon: 'settings' },
 *   { key: 2, value: '옵션 2', leadingIcon: 'trash' }
 * ];
 *
 * <IconButtonDropdown
 *   icon="more"
 *   items={items}
 *   selectedItem={items[0]}
 *   onItemClick={(item) => console.log(item)}
 *   size="md"
 *   align="right"
 * />
 * ```
 */ const IconButtonDropdown = ({ icon, items, selectedItem, onItemClick, size = 'sm', align = 'left', disabled = false })=>{
    const { isListOpen, ref, focusedIndex, handleItemClick, toggleListOpen, handleKeyDown } = useDropdown({
        items,
        onItemClick
    });
    return /*#__PURE__*/ jsxs("div", {
        "data-testid": "design-system-icon-button-dropdown",
        className: "relative w-fit outline-none",
        ref: ref,
        onKeyDown: handleKeyDown,
        tabIndex: 0,
        children: [
            /*#__PURE__*/ jsx(IconButton, {
                onClick: toggleListOpen,
                size: size,
                className: "outline-none",
                disabled: disabled,
                children: /*#__PURE__*/ jsx(SystemIcon, {
                    name: icon,
                    className: twJoin(disabled && 'text-w-gray-300')
                })
            }),
            isListOpen && /*#__PURE__*/ jsx("div", {
                "data-testid": "design-system-icon-button-dropdown--list-container",
                className: twJoin('absolute -bottom-2 z-40 translate-y-full transform transition-all', align === 'right' ? 'right-0' : 'left-0 '),
                children: /*#__PURE__*/ jsx(List.Root, {
                    className: "max-h-full min-w-40 overflow-y-visible",
                    children: items.map((item, index)=>/*#__PURE__*/ jsx(List.Item, {
                            text: item.value,
                            leadingIcon: item.leadingIcon,
                            onClick: ()=>handleItemClick(item),
                            selected: item.key === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.key),
                            isFocused: index === focusedIndex
                        }, item.key))
                })
            })
        ]
    });
};

export { IconButtonDropdown as default };
